import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Plus,
  Trash2,
  ExternalLink,
  History,
  Loader2,
  Upload,
  Search,
  X,
  Settings2,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  generateId,
  type ProjectDocument,
  type ProjectDocumentVersion,
  type DocumentsConfig,
  type DocumentGroup,
  type DocumentType,
  DOC_TYPE_COLOR_PALETTE,
} from '@/store/projectStore';
import {
  projectFileUrl,
  uploadProjectDocumentFile,
  deleteProjectFileOnServer,
} from '@/lib/api';
import { InlineText } from '@/components/InlineEdit';
import { cn } from '@/lib/utils';

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10_240 ? 1 : 0)} КБ`;
  return `${(n / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function VersionIcon({ mime, className, size = 14 }: { mime: string; className?: string; size?: number }) {
  if (mime.startsWith('image/')) return <ImageIcon className={className} size={size} strokeWidth={2} />;
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime.includes('sheet') ||
    mime === 'application/vnd.ms-excel'
  ) {
    return <FileSpreadsheet className={className} size={size} strokeWidth={2} />;
  }
  return <FileText className={className} size={size} strokeWidth={2} />;
}

function TypeBadge({
  type,
  onClick,
  readOnly,
  triggerRef,
}: {
  type: DocumentType | null;
  onClick?: () => void;
  readOnly: boolean;
  triggerRef?: React.Ref<HTMLButtonElement>;
}) {
  const label = type?.label ?? 'Без типа';
  const color = type?.color ?? '#94A3B8';
  const style: React.CSSProperties = {
    background: `${color}1A`,
    borderColor: `${color}55`,
    color,
  };
  const className =
    'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide';
  if (readOnly || !onClick) {
    return (
      <span className={className} style={style}>
        {label}
      </span>
    );
  }
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onClick}
      className={cn(className, 'cursor-pointer transition-colors hover:brightness-95')}
      style={style}
      title="Сменить тип"
    >
      {label}
    </button>
  );
}

// ─── Структура секции: scope, fallback на дефолт, фильтры ───────────────────────

export type DocumentsSectionScope = 'project' | 'global';

export interface DocumentsSectionProps {
  scope: DocumentsSectionScope;
  /** Для проекта — его id; для глобального — «__global__». Используется в URL файла. */
  storageProjectId: string;
  documents: ProjectDocument[];
  config: DocumentsConfig;
  /** Обновление обоих полей разом (для атомарных правок, например структурного редактора). */
  onChange: (next: { documents: ProjectDocument[]; config: DocumentsConfig }) => void;
  readOnly: boolean;
  /** Подписи «Задача плана» на карточках документов проекта. */
  ganttTasks?: { id: string; title: string }[];
  /** Заголовок секции в шапке (например «Документы ДИАКа»). Для встраивания в проект можно не передавать. */
  title?: string;
  /** Короткое пояснение под заголовком. */
  subtitle?: string;
}

export function DocumentsSection({
  scope,
  storageProjectId,
  documents,
  config,
  onChange,
  readOnly,
  ganttTasks,
  title,
  subtitle,
}: DocumentsSectionProps) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [ganttFilter, setGanttFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'date'>('name');
  const [showStructureEditor, setShowStructureEditor] = useState(false);
  // По умолчанию сворачиваем «промежуточные» разделы (например, «Промежуточные
  // версии») — они второстепенны, не должны загромождать список. Остальные
  // развёрнуты. Пользователь может развернуть/свернуть любой раздел вручную.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set((config.groups ?? []).filter((g) => /промежуточн/i.test(g.label || '')).map((g) => g.id)),
  );

  const groupsSorted = useMemo(
    () => [...config.groups].sort((a, b) => a.order - b.order),
    [config.groups],
  );
  const typesById = useMemo(() => new Map(config.types.map((t) => [t.id, t])), [config.types]);

  // Поиск и фильтры применяются к списку. Возвращаем Map groupId → документы.
  const filteredByGroup = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, ProjectDocument[]>();
    for (const g of groupsSorted) map.set(g.id, []);
    // «Документы без группы» (если groupId не входит в config) — складываем в __orphan__.
    const orphanKey = '__orphan__';
    for (const d of documents) {
      if (typeFilter && d.typeId !== typeFilter) continue;
      if (ganttFilter && d.sourceGanttTaskId !== ganttFilter) continue;
      if (q) {
        const titleMatch = d.title.toLowerCase().includes(q);
        const fileMatch = d.versions.some((v) => v.originalName.toLowerCase().includes(q));
        if (!titleMatch && !fileMatch) continue;
      }
      const bucket = map.has(d.groupId) ? d.groupId : orphanKey;
      if (!map.has(bucket)) map.set(bucket, []);
      map.get(bucket)!.push(d);
    }
    // Сортировка внутри группы — по выбранному критерию.
    const latestAt = (d: ProjectDocument): string =>
      (d.versions ?? []).reduce((max, v) => (v.uploadedAt > max ? v.uploadedAt : max), '');
    const byTitle = (a: ProjectDocument, b: ProjectDocument) => a.title.localeCompare(b.title, 'ru');
    const comparator = (a: ProjectDocument, b: ProjectDocument) => {
      if (sortBy === 'type') {
        const la = typesById.get(a.typeId)?.label ?? 'яяя';
        const lb = typesById.get(b.typeId)?.label ?? 'яяя';
        const c = la.localeCompare(lb, 'ru');
        return c !== 0 ? c : byTitle(a, b);
      }
      if (sortBy === 'date') {
        const c = latestAt(b).localeCompare(latestAt(a)); // новые сверху
        return c !== 0 ? c : byTitle(a, b);
      }
      return byTitle(a, b);
    };
    map.forEach((arr) => arr.sort(comparator));
    return map;
  }, [documents, groupsSorted, query, typeFilter, ganttFilter, sortBy, typesById]);

  const totalVisible = useMemo(() => {
    let n = 0;
    filteredByGroup.forEach((arr) => {
      n += arr.length;
    });
    return n;
  }, [filteredByGroup]);

  const newDocInputRef = useRef<HTMLInputElement>(null);
  const versionInputRef = useRef<HTMLInputElement>(null);
  const pendingVersionDocIdRef = useRef<string | null>(null);
  const pendingNewDocGroupRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyVersionDocId, setBusyVersionDocId] = useState<string | null>(null);

  const applyDocuments = (docs: ProjectDocument[]) => onChange({ documents: docs, config });

  const runUpload = async (
    file: File | undefined,
    mode: { type: 'new'; groupId: string } | { type: 'version'; documentId: string },
  ) => {
    if (!file || busy) return;
    setBusy(true);
    if (mode.type === 'version') setBusyVersionDocId(mode.documentId);
    try {
      const version = await uploadProjectDocumentFile(storageProjectId, file);
      if (mode.type === 'new') {
        const docId = generateId('doc');
        const baseTitle = file.name.replace(/\.[^.]+$/, '').trim() || 'Документ';
        applyDocuments([
          ...documents,
          {
            id: docId,
            title: baseTitle,
            groupId: mode.groupId,
            typeId: null,
            versions: [version],
          },
        ]);
        toast.success('Документ добавлен');
      } else {
        applyDocuments(
          documents.map((d) =>
            d.id === mode.documentId ? { ...d, versions: [version, ...d.versions] } : d,
          ),
        );
        toast.success('Новая версия загружена');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить файл');
    } finally {
      setBusy(false);
      setBusyVersionDocId(null);
      if (newDocInputRef.current) newDocInputRef.current.value = '';
      if (versionInputRef.current) versionInputRef.current.value = '';
    }
  };

  const updateDocument = (id: string, patch: Partial<ProjectDocument>) => {
    applyDocuments(documents.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };

  const removeVersionFromDoc = async (doc: ProjectDocument, version: ProjectDocumentVersion) => {
    if (readOnly) return;
    if (typeof window !== 'undefined') {
      const verLabel = version.originalName || `версию ${version.id.slice(0, 8)}`;
      const ok = window.confirm(`Удалить ${verLabel} документа «${doc.title}»? Действие необратимо.`);
      if (!ok) return;
    }
    const rest = doc.versions.filter((v) => v.id !== version.id);
    try {
      await deleteProjectFileOnServer(storageProjectId, version.id);
    } catch {
      toast.error('Файл на сервере не удалён (возможно, уже отсутствует)');
    }
    if (rest.length === 0) {
      applyDocuments(documents.filter((d) => d.id !== doc.id));
    } else {
      applyDocuments(documents.map((d) => (d.id === doc.id ? { ...d, versions: rest } : d)));
    }
    toast.success('Версия удалена');
  };

  const removeDocument = async (doc: ProjectDocument) => {
    if (readOnly) return;
    if (typeof window !== 'undefined') {
      const versionsHint = doc.versions.length > 0 ? ` (вместе со всеми версиями: ${doc.versions.length})` : '';
      const ok = window.confirm(`Удалить документ «${doc.title}»${versionsHint}? Действие необратимо.`);
      if (!ok) return;
    }
    await Promise.allSettled(doc.versions.map((v) => deleteProjectFileOnServer(storageProjectId, v.id)));
    applyDocuments(documents.filter((d) => d.id !== doc.id));
    toast.success('Документ удалён');
  };

  const toggleGroupCollapsed = (gid: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  };

  const ganttTasksWithDocs = useMemo(() => {
    if (scope !== 'project' || !ganttTasks?.length) return [];
    const used = new Set(
      documents.map((d) => d.sourceGanttTaskId).filter((x): x is string => Boolean(x)),
    );
    return ganttTasks.filter((t) => used.has(t.id));
  }, [documents, ganttTasks, scope]);

  return (
    <div className="space-y-4">
      {(title || subtitle) && (
        <div className="flex flex-col gap-1">
          {title ? (
            <h3 className="text-base font-semibold leading-tight" style={{ color: 'var(--nn-dark)' }}>
              {title}
            </h3>
          ) : null}
          {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
      )}

      <input
        ref={newDocInputRef}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          const gid = pendingNewDocGroupRef.current ?? groupsSorted[0]?.id ?? '';
          pendingNewDocGroupRef.current = null;
          if (gid) void runUpload(f, { type: 'new', groupId: gid });
        }}
      />
      <input
        ref={versionInputRef}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          const docId = pendingVersionDocIdRef.current;
          pendingVersionDocIdRef.current = null;
          if (docId && f) void runUpload(f, { type: 'version', documentId: docId });
        }}
      />

      {/* Панель поиска и фильтров */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по названию и имени файла"
            className="w-full rounded-lg border border-border bg-background py-1.5 pl-8 pr-8 text-xs focus:border-[var(--nn-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--nn-blue)]/30"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
              aria-label="Очистить поиск"
            >
              <X size={13} />
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setTypeFilter(null)}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors',
              typeFilter === null
                ? 'border-[var(--nn-blue)] bg-[var(--nn-blue)]/10 text-[var(--nn-blue)]'
                : 'border-border bg-muted/40 text-muted-foreground hover:border-[var(--nn-blue)]/50',
            )}
          >
            Все типы
          </button>
          {config.types.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTypeFilter(typeFilter === t.id ? null : t.id)}
              className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors"
              style={{
                background: typeFilter === t.id ? `${t.color ?? '#94A3B8'}26` : `${t.color ?? '#94A3B8'}0F`,
                borderColor: typeFilter === t.id ? `${t.color ?? '#94A3B8'}AA` : `${t.color ?? '#94A3B8'}44`,
                color: t.color ?? '#64748B',
              }}
            >
              {t.label}
            </button>
          ))}

          <label className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Сортировать:
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'type' | 'date')}
              className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[11px] font-medium normal-case text-foreground"
              title="Порядок документов внутри разделов"
            >
              <option value="name">по названию</option>
              <option value="type">по типу</option>
              <option value="date">по дате (новые сверху)</option>
            </select>
          </label>
        </div>

        {!readOnly ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy || groupsSorted.length === 0}
              onClick={() => {
                pendingNewDocGroupRef.current = groupsSorted[0]?.id ?? null;
                newDocInputRef.current?.click();
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--nn-blue)] px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
              title="Загрузить новый файл в первый раздел"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Добавить документ
            </button>
            <button
              type="button"
              onClick={() => setShowStructureEditor((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                showStructureEditor
                  ? 'border-[var(--nn-blue)] bg-[var(--nn-blue)]/10 text-[var(--nn-blue)]'
                  : 'border-border bg-background text-muted-foreground hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)]',
              )}
              title="Добавить/переименовать группы и типы"
            >
              <Settings2 size={12} />
              Настроить структуру
            </button>
          </div>
        ) : null}
      </div>

      {scope === 'project' && ganttTasksWithDocs.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Задача плана:
          </span>
          <button
            type="button"
            onClick={() => setGanttFilter(null)}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors',
              ganttFilter === null
                ? 'border-[var(--nn-blue)] bg-[var(--nn-blue)]/10 text-[var(--nn-blue)]'
                : 'border-border bg-muted/40 text-muted-foreground hover:border-[var(--nn-blue)]/50',
            )}
          >
            Все
          </button>
          {ganttTasksWithDocs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setGanttFilter(ganttFilter === t.id ? null : t.id)}
              className={cn(
                'max-w-[220px] truncate rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors',
                ganttFilter === t.id
                  ? 'border-[var(--nn-blue)] bg-[var(--nn-blue)]/10 text-[var(--nn-blue)]'
                  : 'border-border bg-muted/40 text-muted-foreground hover:border-[var(--nn-blue)]/50',
              )}
              title={t.title}
            >
              {t.title}
            </button>
          ))}
        </div>
      ) : null}

      {showStructureEditor && !readOnly ? (
        <DocumentsStructureEditor
          config={config}
          documents={documents}
          onChange={(next) => onChange(next)}
          onClose={() => setShowStructureEditor(false)}
        />
      ) : null}

      {totalVisible === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {documents.length === 0
              ? 'Пока нет документов. Добавьте первый файл — он появится здесь с датой и автором загрузки.'
              : 'Под фильтры ничего не подошло. Сбросьте поиск или фильтры.'}
          </p>
          {!readOnly && documents.length === 0 && groupsSorted.length > 0 ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                pendingNewDocGroupRef.current = groupsSorted[0]!.id;
                newDocInputRef.current?.click();
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--nn-blue)] px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Добавить документ
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {groupsSorted.map((group) => {
            const list = filteredByGroup.get(group.id) ?? [];
            if (list.length === 0 && query) return null;
            const collapsed = collapsedGroups.has(group.id);
            return (
              <section
                key={group.id}
                className="overflow-hidden rounded-xl border border-border bg-card/60 shadow-sm"
              >
                <div className="flex items-center justify-between gap-2 border-b border-border/70 bg-muted/20 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapsed(group.id)}
                    className="flex min-w-0 items-center gap-1.5 text-left"
                  >
                    {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                    <span className="truncate text-[12px] font-semibold text-foreground">
                      {group.label}
                    </span>
                    <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                      {list.length}
                    </span>
                  </button>
                  {!readOnly ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        pendingNewDocGroupRef.current = group.id;
                        newDocInputRef.current?.click();
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)] disabled:opacity-50"
                    >
                      {busy && pendingNewDocGroupRef.current === group.id ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Plus size={10} />
                      )}
                      Добавить в раздел
                    </button>
                  ) : null}
                </div>

                {!collapsed ? (
                  <ul className="divide-y divide-border/70">
                    {list.map((doc) => {
                      const latest = doc.versions[0];
                      if (!latest) return null;
                      const openHref = projectFileUrl(storageProjectId, latest.id);
                      const nVer = doc.versions.length;
                      const type = doc.typeId ? typesById.get(doc.typeId) ?? null : null;
                      return (
                        <li key={doc.id} className="group px-2.5 py-1.5 sm:px-3">
                          <div className="flex items-start gap-2.5">
                            <div
                              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/50 text-[var(--nn-blue)] ring-1 ring-border/60"
                              aria-hidden
                            >
                              <VersionIcon mime={latest.mime} />
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                <InlineText
                                  value={doc.title}
                                  onSave={(v) => updateDocument(doc.id, { title: v })}
                                  className="text-[13px] font-semibold leading-tight text-foreground"
                                  inputClassName="text-[13px] font-semibold w-full min-w-0 max-w-xl"
                                  displayAs="span"
                                  placeholder="Название документа"
                                  readOnly={readOnly}
                                />
                                <TypeChooser
                                  types={config.types}
                                  value={doc.typeId}
                                  onChange={(typeId) => updateDocument(doc.id, { typeId })}
                                  readOnly={readOnly}
                                />
                                {!readOnly ? (
                                  <GroupChooser
                                    groups={groupsSorted}
                                    value={doc.groupId}
                                    onChange={(groupId) => updateDocument(doc.id, { groupId })}
                                  />
                                ) : null}
                                {readOnly && type ? <TypeBadge type={type} readOnly /> : null}
                                {nVer > 1 ? (
                                  <span className="rounded bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                                    в.{nVer}
                                  </span>
                                ) : null}
                              </div>

                              <p className="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground">
                                <span className="font-medium text-foreground/80">{latest.uploadedByLogin}</span>
                                {' · '}
                                {formatWhen(latest.uploadedAt)}
                                {' · '}
                                {formatBytes(latest.size)}
                                {' · '}
                                <span className="text-muted-foreground/90">{latest.originalName}</span>
                                {doc.sourceGanttTaskId && ganttTasks?.length ? (
                                  <>
                                    {' · '}
                                    <span className="text-muted-foreground/80">
                                      план:{' '}
                                      <span className="font-medium text-foreground/80">
                                        {ganttTasks.find((t) => t.id === doc.sourceGanttTaskId)?.title ?? doc.sourceGanttTaskId}
                                      </span>
                                    </span>
                                  </>
                                ) : null}
                              </p>

                              {nVer > 1 && (
                                <details className="group/hist mt-1 rounded-md border border-border/60 bg-muted/10">
                                  <summary className="flex cursor-pointer list-none items-center gap-1 px-2 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground [&::-webkit-details-marker]:hidden">
                                    <History size={10} className="shrink-0 opacity-70" />
                                    История версий
                                    <span className="tabular-nums text-muted-foreground/80">({nVer})</span>
                                    <ChevronDown
                                      size={10}
                                      className="ml-auto shrink-0 opacity-70 transition-transform group-open/hist:rotate-180"
                                    />
                                  </summary>
                                  <ul className="border-t border-border/60 px-1.5 py-0.5">
                                    {doc.versions.map((v, idx) => (
                                      <li
                                        key={v.id}
                                        className="flex flex-wrap items-center justify-between gap-1.5 border-b border-border/40 py-1 last:border-b-0"
                                      >
                                        <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">
                                          <span className="font-medium tabular-nums text-foreground">
                                            в.{nVer - idx}
                                          </span>
                                          {' — '}
                                          {v.uploadedByLogin} · {formatWhen(v.uploadedAt)} · {formatBytes(v.size)}
                                        </span>
                                        <span className="flex shrink-0 items-center gap-0.5">
                                          <a
                                            href={projectFileUrl(storageProjectId, v.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/60"
                                            title="Открыть версию"
                                          >
                                            <ExternalLink size={10} />
                                            Открыть
                                          </a>
                                          {!readOnly ? (
                                            <button
                                              type="button"
                                              onClick={() => void removeVersionFromDoc(doc, v)}
                                              className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                              aria-label="Удалить версию"
                                              title="Удалить версию"
                                            >
                                              <Trash2 size={11} />
                                            </button>
                                          ) : null}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </div>

                            <div className="flex shrink-0 items-center gap-0.5">
                              <a
                                href={openHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors',
                                  'text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/60',
                                )}
                                title="Открыть"
                              >
                                <ExternalLink size={12} strokeWidth={2} />
                                <span className="hidden sm:inline">Открыть</span>
                              </a>
                              {!readOnly ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                      pendingVersionDocIdRef.current = doc.id;
                                      versionInputRef.current?.click();
                                    }}
                                    className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)] disabled:opacity-50"
                                    title="Загрузить новую версию"
                                  >
                                    {busy && busyVersionDocId === doc.id ? (
                                      <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                      <Upload size={12} />
                                    )}
                                    <span className="hidden sm:inline">Новая версия</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void removeDocument(doc)}
                                    className="rounded-md p-1 text-muted-foreground opacity-70 transition-colors hover:bg-destructive/10 hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100"
                                    aria-label="Удалить документ"
                                    title="Удалить"
                                  >
                                    <Trash2 size={13} strokeWidth={2} />
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                    {list.length === 0 ? (
                      <li className="px-3 py-3 text-center text-[11px] text-muted-foreground/80">
                        В этом разделе пока пусто.
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </section>
            );
          })}

          {/* «Документы без группы» — если случайно появились documents с groupId вне конфига */}
          {(() => {
            const orphan = filteredByGroup.get('__orphan__');
            if (!orphan || orphan.length === 0) return null;
            return (
              <section className="overflow-hidden rounded-xl border border-dashed border-yellow-400 bg-yellow-50/40 shadow-sm">
                <div className="border-b border-yellow-400/60 bg-yellow-50 px-3 py-2 text-[12px] font-semibold text-yellow-800">
                  Без раздела ({orphan.length}) — назначьте группу через «Настроить структуру»
                </div>
                <ul className="divide-y divide-yellow-200/70">
                  {orphan.map((doc) => {
                    const latest = doc.versions[0];
                    if (!latest) return null;
                    const openHref = projectFileUrl(storageProjectId, latest.id);
                    return (
                      <li key={doc.id} className="group px-3 py-2">
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300/70">
                            <VersionIcon mime={latest.mime} size={12} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <InlineText
                                value={doc.title}
                                onSave={(v) => updateDocument(doc.id, { title: v })}
                                className="text-[12px] font-semibold leading-tight text-yellow-900"
                                inputClassName="text-[12px] font-semibold w-full min-w-0 max-w-xl"
                                displayAs="span"
                                placeholder="Название документа"
                                readOnly={readOnly}
                              />
                              {!readOnly ? (
                                <GroupChooser
                                  groups={groupsSorted}
                                  value={groupsSorted[0]?.id ?? ''}
                                  onChange={(groupId) => updateDocument(doc.id, { groupId })}
                                />
                              ) : null}
                            </div>
                            <p className="mt-0.5 truncate text-[10px] text-yellow-900/80">
                              {latest.uploadedByLogin} · {formatWhen(latest.uploadedAt)} · {formatBytes(latest.size)} ·{' '}
                              {latest.originalName}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-0.5">
                            <a
                              href={openHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/60"
                              title="Открыть"
                            >
                              <ExternalLink size={12} strokeWidth={2} />
                              <span className="hidden sm:inline">Открыть</span>
                            </a>
                            {!readOnly ? (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => {
                                    pendingVersionDocIdRef.current = doc.id;
                                    versionInputRef.current?.click();
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border border-yellow-300/70 bg-white/70 px-1.5 py-1 text-[11px] font-medium text-yellow-900 transition-colors hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)] disabled:opacity-50"
                                  title="Загрузить новую версию"
                                >
                                  {busy && busyVersionDocId === doc.id ? (
                                    <Loader2 size={12} className="animate-spin" />
                                  ) : (
                                    <Upload size={12} />
                                  )}
                                  <span className="hidden sm:inline">Новая версия</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void removeDocument(doc)}
                                  className="rounded-md p-1 text-yellow-900/70 transition-colors hover:bg-destructive/10 hover:text-destructive"
                                  aria-label="Удалить документ"
                                  title="Удалить"
                                >
                                  <Trash2 size={13} strokeWidth={2} />
                                </button>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Выбор типа-бейджа в карточке документа ─────────────────────────────────

function TypeChooser({
  types,
  value,
  onChange,
  readOnly,
}: {
  types: DocumentType[];
  value: string | null;
  onChange: (v: string | null) => void;
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const current = value ? types.find((t) => t.id === value) ?? null : null;

  // Карточки лежат в `<section>` с `overflow-hidden` (нужно, чтобы фон шапки
  // группы корректно вписывался в `rounded-xl`). Поэтому popup рендерится
  // в портал на body и позиционируется `fixed` от bounding rect триггера —
  // иначе его срезает родительская секция, и список «уезжает» под рамку.
  // Решение «вверх или вниз» принимаем по фактической высоте попапа после
  // первого рендера, а не по жёстко заданному max-height.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popupWidth = 224; // w-56
      const margin = 8;
      const gap = 4;
      const actualHeight = popupRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - r.bottom - margin - gap;
      const spaceAbove = r.top - margin - gap;
      // Открываем вверх только если попап реально не помещается снизу
      // и сверху больше места.
      const openUp = actualHeight > spaceBelow && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, openUp ? spaceAbove : spaceBelow);
      let top: number;
      if (openUp) {
        const useHeight = Math.min(actualHeight || maxHeight, maxHeight);
        top = Math.max(margin, r.top - useHeight - gap);
      } else {
        top = r.bottom + gap;
      }
      let left = r.left;
      if (left + popupWidth > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - popupWidth - margin);
      }
      setPos({ left, top, width: popupWidth, maxHeight });
    };
    update();
    // После первого рендера измеряем реальную высоту и пересчитываем.
    const raf = requestAnimationFrame(update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (readOnly) return <TypeBadge type={current} readOnly />;
  return (
    <>
      <TypeBadge
        type={current}
        onClick={() => setOpen((v) => !v)}
        readOnly={false}
        triggerRef={triggerRef}
      />
      {open && pos
        ? createPortal(
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setOpen(false)}
                aria-hidden
              />
              <div
                ref={popupRef}
                className="fixed z-[61] overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg"
                style={{
                  left: pos.left,
                  top: pos.top,
                  width: pos.width,
                  maxHeight: pos.maxHeight,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-muted"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/40" />
                  Без типа
                </button>
                {types.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      onChange(t.id);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] hover:bg-muted"
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: t.color ?? '#94A3B8' }}
                    />
                    {t.label}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}

function GroupChooser({
  groups,
  value,
  onChange,
}: {
  groups: DocumentGroup[];
  value: string;
  onChange: (gid: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground hover:border-[var(--nn-blue)]/50 focus:border-[var(--nn-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--nn-blue)]/30"
      title="Переместить в другой раздел"
    >
      {groups.map((g) => (
        <option key={g.id} value={g.id}>
          {g.label}
        </option>
      ))}
    </select>
  );
}

// ─── Редактор структуры: группы и типы-бейджи ───────────────────────────────

function DocumentsStructureEditor({
  config,
  documents,
  onChange,
  onClose,
}: {
  config: DocumentsConfig;
  documents: ProjectDocument[];
  onChange: (next: { documents: ProjectDocument[]; config: DocumentsConfig }) => void;
  onClose: () => void;
}) {
  const groupsSorted = useMemo(
    () => [...config.groups].sort((a, b) => a.order - b.order),
    [config.groups],
  );

  const setConfig = (next: DocumentsConfig, nextDocs: ProjectDocument[] = documents) => {
    // Пересчёт order — чтобы всегда был 0..N-1 без дырок.
    const reordered = [...next.groups].sort((a, b) => a.order - b.order).map((g, i) => ({ ...g, order: i }));
    onChange({ documents: nextDocs, config: { groups: reordered, types: next.types } });
  };

  const addGroup = () => {
    const id = generateId('dg');
    const maxOrder = groupsSorted.length ? Math.max(...groupsSorted.map((g) => g.order)) : -1;
    setConfig({
      ...config,
      groups: [...config.groups, { id, label: 'Новый раздел', order: maxOrder + 1 }],
    });
  };

  const renameGroup = (gid: string, label: string) => {
    setConfig({
      ...config,
      groups: config.groups.map((g) => (g.id === gid ? { ...g, label } : g)),
    });
  };

  const moveGroup = (gid: string, dir: -1 | 1) => {
    const idx = groupsSorted.findIndex((g) => g.id === gid);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= groupsSorted.length) return;
    const next = [...groupsSorted];
    const [a, b] = [next[idx]!, next[j]!];
    next[idx] = { ...b, order: idx };
    next[j] = { ...a, order: j };
    setConfig({ ...config, groups: next });
  };

  const removeGroup = (gid: string) => {
    const group = config.groups.find((g) => g.id === gid);
    if (!group) return;
    const docsInGroup = documents.filter((d) => d.groupId === gid);
    if (docsInGroup.length > 0) {
      const remaining = config.groups.filter((g) => g.id !== gid);
      if (remaining.length === 0) {
        toast.error('Нельзя удалить единственную группу.');
        return;
      }
      const defaultTarget = remaining[0]!.id;
      const targetId = window.prompt(
        `В разделе «${group.label}» есть ${docsInGroup.length} документ(ов). Укажите id раздела, куда их переместить (по умолчанию «${remaining[0]!.label}»). Доступно: ${remaining.map((g) => `${g.id} (${g.label})`).join(', ')}`,
        defaultTarget,
      );
      const target = targetId && remaining.some((g) => g.id === targetId) ? targetId : defaultTarget;
      const nextDocs = documents.map((d) => (d.groupId === gid ? { ...d, groupId: target } : d));
      setConfig(
        { ...config, groups: config.groups.filter((g) => g.id !== gid) },
        nextDocs,
      );
      toast.success('Раздел удалён, документы перенесены');
      return;
    }
    setConfig({ ...config, groups: config.groups.filter((g) => g.id !== gid) });
  };

  const addType = () => {
    const id = generateId('dt');
    const color = DOC_TYPE_COLOR_PALETTE[config.types.length % DOC_TYPE_COLOR_PALETTE.length]!;
    setConfig({ ...config, types: [...config.types, { id, label: 'Новый тип', color }] });
  };

  const updateType = (tid: string, patch: Partial<DocumentType>) => {
    setConfig({
      ...config,
      types: config.types.map((t) => (t.id === tid ? { ...t, ...patch } : t)),
    });
  };

  const removeType = (tid: string) => {
    const usage = documents.filter((d) => d.typeId === tid).length;
    if (usage > 0) {
      const ok = window.confirm(
        `Этот тип присвоен ${usage} документ(ам). После удаления у них пропадёт бейдж типа. Продолжить?`,
      );
      if (!ok) return;
    }
    const nextDocs = documents.map((d) => (d.typeId === tid ? { ...d, typeId: null } : d));
    setConfig({ ...config, types: config.types.filter((t) => t.id !== tid) }, nextDocs);
  };

  return (
    <div className="space-y-4 rounded-xl border border-[var(--nn-blue)]/30 bg-[var(--nn-blue)]/5 p-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Настройка структуры документов</h4>
          <p className="text-[11px] text-muted-foreground">
            Здесь можно поменять названия и порядок разделов, а также управлять типами-бейджами. Всё
            сохраняется прямо в этом рабочем пространстве — без правок кода.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          aria-label="Скрыть редактор"
        >
          <X size={14} />
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
              Разделы (дорожки)
            </h5>
            <button
              type="button"
              onClick={addGroup}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)]"
            >
              <Plus size={10} /> Раздел
            </button>
          </div>
          <ul className="space-y-1 rounded-lg border border-border bg-card/80">
            {groupsSorted.map((g, i) => (
              <li
                key={g.id}
                className="flex items-center gap-2 border-b border-border/60 px-2 py-1.5 last:border-b-0"
              >
                <GripVertical size={12} className="shrink-0 text-muted-foreground/60" />
                <InlineText
                  value={g.label}
                  onSave={(v) => renameGroup(g.id, v)}
                  className="flex-1 text-[12px] font-medium text-foreground"
                  inputClassName="text-[12px] w-full"
                  placeholder="Название раздела"
                />
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => moveGroup(g.id, -1)}
                    className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-30"
                    aria-label="Выше"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    disabled={i === groupsSorted.length - 1}
                    onClick={() => moveGroup(g.id, 1)}
                    className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-30"
                    aria-label="Ниже"
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeGroup(g.id)}
                    className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    aria-label="Удалить раздел"
                    title="Удалить раздел"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
              Типы (бейджи)
            </h5>
            <button
              type="button"
              onClick={addType}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground hover:border-[var(--nn-blue)] hover:text-[var(--nn-blue)]"
            >
              <Plus size={10} /> Тип
            </button>
          </div>
          <ul className="space-y-1 rounded-lg border border-border bg-card/80">
            {config.types.map((t) => (
              <li
                key={t.id}
                className="flex items-center gap-2 border-b border-border/60 px-2 py-1.5 last:border-b-0"
              >
                <ColorSwatch
                  value={t.color ?? '#94A3B8'}
                  onChange={(c) => updateType(t.id, { color: c })}
                />
                <InlineText
                  value={t.label}
                  onSave={(v) => updateType(t.id, { label: v })}
                  className="flex-1 text-[12px] font-medium text-foreground"
                  inputClassName="text-[12px] w-full"
                  placeholder="Название типа"
                />
                <button
                  type="button"
                  onClick={() => removeType(t.id)}
                  className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Удалить тип"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
            {config.types.length === 0 ? (
              <li className="px-3 py-3 text-center text-[11px] text-muted-foreground">
                Пока нет типов. Добавьте, например, «Регламент» или «Шаблон».
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ColorSwatch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-4 w-4 shrink-0 rounded-full border border-border"
        style={{ background: value }}
        aria-label="Выбрать цвет"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute left-0 top-full z-50 mt-1 grid grid-cols-4 gap-1 rounded-lg border border-border bg-popover p-1.5 shadow-lg">
            {DOC_TYPE_COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                className="h-5 w-5 rounded-full border border-border ring-0 transition-[box-shadow] hover:ring-2 hover:ring-[var(--nn-blue)]/30"
                style={{ background: c }}
                aria-label={c}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
